import React, { useEffect, useState, useRef } from 'react';
import { array, bool, func, number, object, string, shape } from 'prop-types';
import classnames from 'classnames';
import FacetPanel from 'ps-components/FacetPanel/FacetPanel';
import FacetPanelLoading from 'ps-components/FacetPanel/FacetPanelLoading/FacetPanelLoading';
import Pagination from 'ps-components/Pagination/Pagination';
import Placeholder from '../Placeholder/Placeholder';
import Search from 'ps-components/Search/Search';
import getHiddenFacets from 'ps-components/FacetPanel/getHiddenFacets';
import LoadingTable from 'ps-components/AdvancedTable/LoadingTable/LoadingTable';
import SlottedSearchGrid from './SlottedGrid/SlottedGrid';

import { areAllFacetsChecked } from '../../../lib/facetHelpers';

SlottedJobSearch.propTypes = {
  masterAccountId: string,
  updatePricingGrid: func,
  actions: object,
  slottedSearch: shape({
    slottedSearchSelectedView: shape({
      selectedView: object,
      loading: bool,
      loaded: bool,
      status: number,
      error: string
    }),
    slottedSearchJobDetails: object,
    slottedSearchViews: shape({
      views: array,
      loading: bool,
      loaded: bool,
      status: number,
      error: string
    }),
    setSlottedSearchSelectedView: shape({
      loading: bool
    }),
    currency: string
  }),
  facet: object,
  grid: object,
  location: object,
  currency: string,
  currentJob: object,
  displayIn: string,
  activeBenchmarkYear: number,
  selectedBenchmarkYear: number,
  addMatch: func,
  deleteMatch: func,
  updateModifiedDate: func,
  onMatchSelect: func,
  selectedRowId: number,
  hideSelect: bool,
  loadingId: number,
  showSlideout: func,
  refreshJobTabData: func,
  toggleHeader: func,
  showMinHeader: bool,
  history: object,
  readOnlyUser: bool,
  replacementTokens: array,
  useJobCodeKey: bool,
  isMatchDeleting: bool,
  selectedSlottedJobs: array,
  viewLoading: bool,
  aging: string,
  numericRounding: number,
  percentRounding: number
};

function SlottedJobSearch(props) {
  const [searchText, setSearchText] = useState('');
  const [firstPageLoaded, setFirstPageLoaded] = useState(false);
  const [facetCollapse, setFacetCollapse] = useState(true);
  const searchInputRef = useRef();

  const {
    facetSelection,
    facets,
    limit,
    loaded,
    loading,
    name: jobFacetName,
    offset,
    query,
    searchResultsCount: totalCount,
    showZeros
  } = props.facet;

  useEffect(() => {
    if (firstPageLoaded && !loading) {
      setSearchText(query);
      searchInputRef.current.setSearchValue(query);
      props.actions.searchInputSearch(query);
    }
  }, [
    query,
    props.grid?.data?.length ? props.location.search : '',
    props.slottedSearch?.slottedSearchSelectedView?.selectedViewPk,
    props.displayIn,
    props.slottedSearch?.currency
  ]);

  useEffect(() => {
    setFirstPageLoaded(true);

    if (!loading) {
      setSearchText(query || '');
      searchInputRef.current.setSearchValue(query || '');
      props.actions.searchInputSearch(query || '', { firstPageLoad: true });
    }
  }, []);

  // scrollRef used to scroll up to top after search or pagination change
  let scrollRef = React.createRef();

  // Determine job code key text replacement text
  const hasJobCodeKeyReplacement = props.replacementTokens?.find(value => value.token === 'job_code_key_text');
  const jobCodeKeyText = hasJobCodeKeyReplacement?.value || 'Job Code Key';

  // Determine whether views, default view, or grid data are loading
  const isLoading =
    props.slottedSearch.slottedSearchViews?.loading ||
    props.slottedSearch.slottedSearchSelectedView?.loading ||
    props.slottedSearch.setSlottedSearchSelectedView?.loading ||
    props.grid.processInitialLoad ||
    props.viewLoading;

  // Show 'Reset All' button in facet panel header if any options are unselected in facet panel
  const anyFacetsUnselected = areAllFacetsChecked(facets, facetSelection);
  const showResetAllButton = anyFacetsUnselected || (searchText && searchText !== '');

  // Logic for Reset All button
  function handleReset() {
    setSearchText('');
    searchInputRef.current.setSearchValue('');
    props.actions.handleReset();
  }

  // Pagination logic for org job list
  function pageOrgJobs(limit, offset) {
    props.actions.pageList(limit, offset);
  }

  // clicking x to clear search text
  function searchOnClick() {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset pagination scroll
    }
    props.actions.setSearchQuery('');
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      if (scrollRef?.current?.scrollTop) {
        scrollRef.current.scrollTop = 0; // reset pagination scroll
      }
      props.actions.setSearchQuery(event.target.value);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearch() {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset pagination scroll
    }
    props.actions.setSearchQuery(searchText);
  }

  // Checks for empty facet or org job results
  const showPlaceHolder =
    (props.grid?.data?.length === 0 || (props.grid?.data && props.grid?.data[0].length === 0)) && loaded && !loading;

  // Show if no orgs jobs are returned
  const noResultsPlaceholder = (
    <Placeholder
      containerClassName="slotted-search__no-results-container"
      primaryText={'There are no jobs found for the selected criteria'}
      secondaryText={'Please try different critera, group, or currency'}
    />
  );

  return (
    <div className="slotted-search">
      {isLoading ? (
        <FacetPanelLoading
          className={'slotted-search__facet-panel org__job-facets'}
          numberOfFacets={5}
          numberOfSubItems={5}
          includeHeader={true}
        />
      ) : (
        <FacetPanel
          className="slotted-search__facet-panel org__job-facets"
          id="slotted-search__facet-panel"
          facetItemClick={props.actions.facetItemClick}
          facetReset={props.actions.facetReset}
          facetSelection={facetSelection}
          facets={facets}
          hiddenFacets={getHiddenFacets('slottedSearch', props.useJobCodeKey)}
          isDisabled={false}
          isLoaded={loaded}
          limit={5}
          resetSearch={handleReset}
          showFacetSectionSearch
          showResetButton={showResetAllButton || false}
          showZeros={showZeros}
          firstPageLoad={!firstPageLoaded}
          canCollapse={true}
          collapseCallback={setFacetCollapse}
          collapseTooltip={facetCollapse ? 'Close slotted job search filters' : 'Open slotted job search filters'}
        />
      )}

      <div className="slotted-search__content">
        <div className="slotted-search__search-container">
          <Search
            className={classnames('slotted-search__search', { 'facet-offset': !facetCollapse })}
            onClick={searchOnClick}
            onKeyDown={searchOnKeyDown}
            onSearch={searchOnSearch}
            placeholder="Search by job title or job code"
            testId="slotted-search__search"
            useInternalState
            ref={searchInputRef}
          />
        </div>

        {isLoading ? (
          <LoadingTable className="slotted-search__loading-table" numberOfRows={5} includeHeader={true} />
        ) : showPlaceHolder ? (
          noResultsPlaceholder
        ) : (
          <React.Fragment>
            <div className="slotted-search__list-container" ref={scrollRef}>
              <SlottedSearchGrid
                history={props.history}
                masterAccountId={props.masterAccountId}
                slottedSearchGrid={props.grid}
                slottedSearchGridActions={props.actions}
                readOnlyUser={props.readOnlyUser}
                restrictedUser={false}
                offset={offset}
                useJobCodeKey={props.useJobCodeKey}
                jobCodeKeyText={jobCodeKeyText}
                loading={loading}
                searchQuery={query}
                loadingId={props.loadingId}
                isMatchDeleting={props.isMatchDeleting}
                selectedSlottedJobs={props.selectedSlottedJobs}
                addMatch={props.addMatch}
                deleteMatch={props.deleteMatch}
                currentJob={props.currentJob}
                selectedBenchmarkYear={props.selectedBenchmarkYear}
                updateModifiedDate={props.updateModifiedDate}
                onMatchSelect={props.onMatchSelect}
                refreshJobTabData={props.refreshJobTabData}
                hideSelect={props.hideSelect}
                aging={props.aging}
                numericRounding={props.numericRounding}
                percentRounding={props.percentRounding}
              />
            </div>
            <div className="slotted-search__pagination-container">
              <Pagination
                className="slotted-search__pagination"
                offset={offset}
                pageAction={pageOrgJobs}
                pageShowRange={3}
                scrollEl={scrollRef}
                scrollTopPageChange
                shouldShowInput
                step={limit}
                totalCount={totalCount}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default SlottedJobSearch;
